/**
 *
 * Copyright (c) 2019-present, wobe-systems GmbH
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * to be found in the LICENSE file in the root directory of this source tree.
 */

import * as flowTypes from '@/store/flow/models';
import * as userTypes from '@/store/user/models';

// eslint-disable-next-line no-control-regex
export const EMAIL_REGEX = /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i;

/**
 * The Regex for setting a password
 *
 * Rules are:
 *
 * 1. Minimum 8 characters
 * 2. At least one lowercase alphabet
 * 3. At least one uppercase alphabet
 * 4. At least one number
 * 5. At least one special character
 */
export const PASSWORD_REGEX = /^(?=.*?[A-ZÀ-Ý])(?=.*?[a-zà-ÿ])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;

/**
 * The regex for a username
 *
 * Rules are:
 *
 * 1. Minimum 3 characters
 * 2. Maximum 20 characters
 * 3. Allowed special characters are: . - _
 */
export const USERNAME_REGEX = /^[a-zA-Z0-9À-ž._-]{3,20}$/;

/**
 * BASE64 Prefix for Images
 * that are converted from SVG to BASE64
 */
export const SVG_TO_BASE64_PREFIX = 'data:image/svg+xml;base64,';

/**
 * LINK TO DOCUMENTATION
 */
export const LINK_TO_DOCUMENTATION = 'https://doc.industrial-devops.org/titanDocumentation/tutorials/firstFlow/';
/**
 * LINK TO SUPPORT
 */
export const LINK_TO_SUPPORT = 'https://wobe-systems.atlassian.net/servicedesk/customer/portal/3';
/**
 * MessageType Builder RegEx
 *
 * - Expects to start with an alphabet or _
 * - Allows numbers
 * - Allows an underscore _ and a hyphen -
 * - Does not allow special characters other than above mentioned
 * - Does not allow space
 * - Does not allow double underscores/hyphens or combination of both
 *
 * Valid: post_2, post_2-3, _PosT
 *
 * Invalid: -post, post_-2, post__4, post@33
 */
export const MESSAGE_TYPE_REGEX = /^(?!.*__.*)[a-zA-Z_][a-zA-Z-_0-9]*$/;

/**
 * Follows the same rules as @see MESSAGE_TYPE_REGEX
 * with the following addition:
 *
 * - Allows = and ; in the middle of the name
 */
export const TYPE_LIBRARY_REGEX = /^(?!.*__.*)[a-zA-Z_][a-zA-Z-_=;0-9]*$/;

export const FLOAT_REGEX = /^[+-]?([0-9]+([.][0-9]*)?|[.][0-9]+)$/;
export const INT_REGEX = /^[+-]?([0-9]+)$/;
export const UINT_REGEX = /^([0-9]+)$/;

/**
 * disable flow editing
 * for all flows: if they are running
 */
export const disableFlowEditing = (flow:flowTypes.Flow) => flow.running;

/**
 * Identifier for a mapping rule using a
 * port as the source
 */
export const PORT_MAPPING_RULE = 'port';

/**
 * Prefix for an ID of a constant
 */
export const CONSTANT_ID_PREFIX = 'const-';

/**
 * Prefix for a mapping rule using a
 * constant
 */
export const CONSTANT_MAPPING_PREFIX = '_constant';

/**
 * Identifier for a mapping rule using a
 * constant
 */
export const CONSTANT_MAPPING_RULE = 'constant';

/**
 * Prefix for an ID of a buffer element
 */
export const BUFFER_ID_PREFIX = 'buff-';

/**
 * Prefix for a mapping rule using
 * a buffer element
 */
export const BUFFER_MAPPING_PREFIX = '_buffer';

/**
 * Identifier for a mapping rule using a
 * buffer
 */
export const BUFFER_MAPPING_RULE = 'buffer';

/**
 * Prefix for an ID of a type
 */
export const TYPE_ID_PREFIX = 'type-';

/**
 * Name of type in bricks that needs
 * decryption
 */
export const SECRET_TYPE = 'secret';

/**
 * Name of the type in bricks that need
 * an editor with a text area
 */
export const EDITOR_TEXT_AREA = 'textarea';

/**
 * Name of the type in bricks that need
 * an editor to append item to a StringList
 */
export const EDITOR_STRING_LIST = 'StringList';

/**
 * Regex for API path
 *
 * - Cannot contain spaces
 * - Cannot contain special characters
 */
export const API_PATH_REGEX = /^(\d|\w)+$/;

/**
 * Regex for StringList editor
 *
 * The elements:
 *
 * - Cannot contain a pipe
 */
export const STRING_LIST_ELEMENT_REGEX = /^[^|]*$/;

/**
 * Defines mode of operation for a component
 * so it can be properly re-used for said
 * operation
 */
export const modes = {
  EDIT: 'edit',
  ADD: 'add',
};
